import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Button from '@deprecated-ui/common/Button';
import color from '@deprecated-ui/_style/color';
import numeral from 'numeral';
import { useDispatch, useSelector } from 'react-redux';
import Image from 'global/Image';
import If from 'global/ErrorHandlers/If';

import { showTradingModalWithAuth } from 'features/securities/AuthModal/utils';
import { AUTH_ORDER_TYPE } from 'features/securities/AuthModal/constants';
import { withMaintenanceGuard } from 'global/Securities/utils/maintenance';
import { effects, selectors } from '../slice';
import {
  ButtonVote,
  Result,
  ResultWrapper,
  TargetPriceWrapper,
  TextBorder,
} from './styled';
import { targetPriceType } from './constants';

interface TargetPriceContainerProps {
  targetPrice: any;
  postid: number;
  isAuthor?: number;
}

type AgreeType = 0 | 1;
type AgreeValueType = 'agree' | 'disagree';

function TargetPriceContainer({
  targetPrice,
  postid,
  isAuthor,
}: TargetPriceContainerProps) {
  const dispatch = useDispatch();

  // select data from slice
  const { postVoteTargetPrice } = useSelector(selectors);
  const { isLoading } = postVoteTargetPrice;

  // price gap
  const {
    duration,
    id,
    tradeable,
    voted,
    day_left: dayLeft,
    last_price: last,
    target_price: target,
  } = targetPrice;

  const isHit = targetPrice.hit === 1;
  const isMissed = targetPrice.hit === 2;
  const canBuy = targetPrice.hit === 0 && tradeable === 1;
  const isFinished = isHit || isMissed || dayLeft <= 0;

  const differentPercentage = ((target - last) / last) * 100;
  const targetType = differentPercentage < 0 ? 'bearish' : 'bullish';

  // vote percentage
  const { agree, disagree } = targetPrice;
  const totalVote = agree + disagree;
  const agreePercentage = (agree / totalVote) * 100;
  const disagreePercentage = (disagree / totalVote) * 100;

  const votedValueMapper: Record<AgreeValueType, AgreeType> = {
    agree: 1,
    disagree: 0,
  };

  const handleVoteTargetPrice = (agreeVal: AgreeValueType) => {
    const data = {
      targetPriceId: id,
      agree: votedValueMapper[agreeVal],
      postid,
    };
    dispatch(effects.postVoteTargetPrice(data));
  };

  const handleClickButtonBuy = () =>
    withMaintenanceGuard(() => {
      showTradingModalWithAuth({
        symbol: targetPrice.symbol2 || targetPrice.symbol,
        orderType: AUTH_ORDER_TYPE.BUY,
      });
    });

  return (
    <TargetPriceWrapper data-cy="stream-target-price" targetType={targetType}>
      <Flex justify="space-between">
        <div>
          <Text size="10px">MY {targetType.toUpperCase()} PREDICTION</Text>
          <Flex align="center" wrap="wrap" padding="0 10px 0 0">
            <Text
              weight="600"
              family="bold"
              height="30px"
              size="22px"
              padding="5px 5px 2px 0"
            >
              {targetPrice.symbol2 || targetPrice.symbol}
            </Text>
            <Text color={color.heather}>{targetPrice.name}</Text>
          </Flex>
          <Flex align="flex-end" margin="15px 0">
            <Text
              weight="600"
              family="bold"
              height="38px"
              size="38px"
              padding="5px 5px 2px 0"
            >
              {numeral(targetPrice.target_price).format('0,0')}
            </Text>
            <Text height="38px" style={{ whiteSpace: 'nowrap' }}>
              Target Price
            </Text>
          </Flex>
        </div>

        <If condition={isHit}>
          <div>
            <Image
              alt=""
              src="https://stockbit.com/assets/img/correct.png"
              style={{ width: '80px' }}
            />
          </div>
        </If>

        <If condition={isMissed}>
          <div>
            <Image
              alt=""
              src="https://stockbit.com/assets/img/missed.png"
              style={{ width: '80px' }}
            />
          </div>
        </If>

        <If condition={canBuy}>
          <div style={{ width: '100px' }}>
            <Button
              data-cy="stream-target-price-buy"
              type="primary"
              // @ts-expect-error
              round
              block
              onClick={handleClickButtonBuy}
            >
              <Text size="18px" color={color.light} align="center">
                BUY
              </Text>
            </Button>
          </div>
        </If>
      </Flex>

      <Flex justify="space-between" padding="0 20px 20px 0">
        <div>
          <TextBorder>Initial Price</TextBorder>
          <Text>{numeral(targetPrice.last_price).format('0,0')}</Text>
        </div>
        <div>
          <TextBorder>{targetPriceType[targetType].text}</TextBorder>
          <Text color={targetPriceType[targetType].color}>
            {numeral(differentPercentage).format('0.00')}%
          </Text>
        </div>
        <div>
          <TextBorder>Timing</TextBorder>
          <Text>
            {`${duration} days`}{' '}
            {isFinished
              ? '(Finished)'
              : `(${dayLeft} day${dayLeft > 1 ? 's' : ''} left)`}
          </Text>
        </div>
      </Flex>

      {voted !== '' || isFinished || isAuthor ? (
        <ResultWrapper>
          <Result background={voted === 'disagree' && color.coralCandy}>
            <Text
              align="center"
              size="16px"
              weight="600"
              family="bold"
              color={color.sunsetOrange}
            >
              {numeral(disagreePercentage).format('0,0')}% Disagree
            </Text>
            <Text
              size="12px"
              color={color.dimgray}
              align="center"
              padding="3px"
            >
              {targetPrice.disagree} votes
            </Text>
          </Result>
          <Result background={voted === 'agree' && color.hummingBird}>
            <Text
              align="center"
              size="16px"
              weight="600"
              family="bold"
              color={color.primary}
            >
              {numeral(agreePercentage).format('0,0')}% Agree
            </Text>
            <Text
              size="12px"
              color={color.dimgray}
              align="center"
              padding="3px"
            >
              {targetPrice.agree} votes
            </Text>
          </Result>
        </ResultWrapper>
      ) : (
        <Flex>
          <div style={{ width: '50%', paddingRight: '5px' }}>
            <ButtonVote
              type="bearish"
              disabled={isLoading}
              round
              onClick={() => handleVoteTargetPrice('disagree')}
            >
              <Image alt="" src="https://stockbit.com/assets/img/stpdown.png" />
              <Text align="center">Disagree</Text>
            </ButtonVote>
          </div>
          <div style={{ width: '50%', paddingLeft: '5px' }}>
            <ButtonVote
              disabled={isLoading}
              type="bullish"
              round
              onClick={() => handleVoteTargetPrice('agree')}
            >
              <Image alt="" src="https://stockbit.com/assets/img/stpup.png" />
              <Text align="center">Agree</Text>
            </ButtonVote>
          </div>
        </Flex>
      )}
    </TargetPriceWrapper>
  );
}

export default TargetPriceContainer;
